/**
 * What a harness type can do that the deployment's type list does not say.
 *
 * Keyed by the type's stable name. This is behaviour, never identity: which
 * types exist, and how each is worded, come from `GET /api/harness-types`
 * (spec 240 FR-003/FR-004). A type with no entry — one this build has never
 * seen — gets `DEFAULT_CAPABILITIES`, so it still appears in every picker and
 * works with the plain form.
 */
export interface HarnessCapabilities {
  /** Shows the wire API choice and sends `wire_api`. */
  hasWireApi: boolean;
  /** Where a profile's model comes from. */
  modelSource: 'free-text' | 'claude-catalog' | 'openai-catalog';
  /** Pre-filled into an empty Base URL field when this type is picked (spec 160 FR-002). */
  defaultBaseUrl?: string;
  /** Placeholder for the connection name field. */
  namePlaceholder?: string;
}

export const DEFAULT_CAPABILITIES: HarnessCapabilities = {
  hasWireApi: false,
  modelSource: 'free-text',
};

const CAPABILITIES: Record<string, HarnessCapabilities> = {
  // The org's Claude connection runs through this gateway, not api.anthropic.com directly.
  'claude-code': {
    hasWireApi: false,
    modelSource: 'claude-catalog',
    defaultBaseUrl: 'https://anthropic.generative.engine.capgemini.com',
    namePlaceholder: 'Global Claude',
  },
  // Likewise the org's OpenAI connection, not api.openai.com directly.
  codex: {
    hasWireApi: true,
    modelSource: 'openai-catalog',
    defaultBaseUrl: 'https://openai.generative.engine.capgemini.com/v1',
    namePlaceholder: 'Global OpenAI',
  },
};

export function capabilitiesOf(
  harnessType: string | undefined,
): HarnessCapabilities {
  if (harnessType === undefined) return DEFAULT_CAPABILITIES;
  return CAPABILITIES[harnessType] ?? DEFAULT_CAPABILITIES;
}
